import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  CONDITION_OPS,
  ConditionFieldDef,
  ConditionFilterResult,
  ConditionJoinMode,
  ConditionOpDef,
  FilterCondition,
  conditionFieldById,
  describeCondition,
  isConditionComplete,
  newCondition,
  opsForField,
} from './condition-filters';

/**
 * Chip + popover condition builder, shared by the Item Scorer Summary style
 * filter bars. The host owns the `conditions` array and the field catalog; this
 * component only edits them and reports that something changed.
 */
@Component({
  selector: 'condition-filter-bar',
  templateUrl: './condition-filter-bar.component.html',
  styleUrls: ['./condition-filter-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConditionFilterBarComponent implements OnInit, OnChanges {
  @Input() fields: ConditionFieldDef[] = [];
  @Input() conditions: FilterCondition[] = [];
  @Input() joinMode: ConditionJoinMode = 'AND';
  @Input() disabled = false;
  @Input() rowLabel = 'Filters';
  /** Optional summary from applyConditionStack, rendered under the chip row. */
  @Input() summary: ConditionFilterResult = null;
  @Input() summaryNoun = 'items';

  @Output() conditionsChange = new EventEmitter<FilterCondition[]>();
  @Output() joinModeChange = new EventEmitter<ConditionJoinMode>();
  @Output() filterChange = new EventEmitter<void>();

  openConditionId: string = null;
  isAdvancedOpen = false;
  /** Stable reference for the field picker — rebuilt only when `fields` changes. */
  fieldGroupsList: { group: string; fields: ConditionFieldDef[] }[] = [];
  private opsByFieldId = new Map<string, ConditionOpDef[]>();

  ngOnInit(): void {
    this.rebuildFieldGroups();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fields) {
      this.rebuildFieldGroups();
    }
  }

  fieldOf(condition: FilterCondition): ConditionFieldDef {
    return conditionFieldById(this.fields, condition?.field);
  }

  trackByConditionId(_index: number, condition: FilterCondition): string {
    return condition.id;
  }

  trackByFieldGroup(_index: number, bucket: { group: string }): string {
    return bucket.group || '__default__';
  }

  trackByFieldId(_index: number, field: ConditionFieldDef): string {
    return field.id;
  }

  trackByOpId(_index: number, op: ConditionOpDef): string {
    return op.id;
  }

  opsOf(condition: FilterCondition): ConditionOpDef[] {
    const field = this.fieldOf(condition);
    if (!field) {
      return [];
    }
    if (!this.opsByFieldId.has(field.id)) {
      this.opsByFieldId.set(field.id, opsForField(field));
    }
    return this.opsByFieldId.get(field.id);
  }

  private rebuildFieldGroups(): void {
    this.opsByFieldId.clear();
    const groups = new Map<string, ConditionFieldDef[]>();
    for (const field of this.fields || []) {
      const key = field.group || '';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(field);
    }
    this.fieldGroupsList = [...groups.entries()].map(([group, fields]) => ({ group, fields }));
  }

  argCount(condition: FilterCondition): number {
    const op = CONDITION_OPS[condition?.op];
    return op ? op.args : 0;
  }

  isListOp(condition: FilterCondition): boolean {
    return condition?.op === 'in' || condition?.op === 'notIn';
  }

  isConditionActive(condition: FilterCondition): boolean {
    return isConditionComplete(this.fields, condition);
  }

  conditionChipLabel(condition: FilterCondition): string {
    return this.fieldOf(condition)?.label || 'New filter';
  }

  conditionChipValue(condition: FilterCondition): string {
    const described = describeCondition(this.fields, condition);
    if (!described) {
      return '';
    }
    const label = this.fieldOf(condition)?.label || '';
    return described.startsWith(label) ? described.slice(label.length).trim() : described;
  }

  addCondition(): void {
    if (this.disabled) {
      return;
    }
    const condition = newCondition();
    this.conditions.push(condition);
    this.openConditionId = condition.id;
    this.emit();
  }

  removeCondition(condition: FilterCondition): void {
    if (this.disabled) {
      return;
    }
    const i = this.conditions.indexOf(condition);
    if (i > -1) {
      this.conditions.splice(i, 1);
    }
    if (this.openConditionId === condition.id) {
      this.openConditionId = null;
    }
    this.emit();
  }

  clearConditions(): void {
    if (this.disabled) {
      return;
    }
    this.conditions.splice(0, this.conditions.length);
    this.openConditionId = null;
    this.emit();
  }

  toggleConditionEditor(id: string): void {
    this.openConditionId = this.openConditionId === id ? null : id;
  }

  closeConditionEditor(): void {
    this.openConditionId = null;
  }

  toggleAdvanced(): void {
    this.isAdvancedOpen = !this.isAdvancedOpen;
  }

  setJoinMode(mode: ConditionJoinMode): void {
    if (this.disabled) {
      return;
    }
    this.joinMode = mode;
    this.joinModeChange.emit(mode);
    this.filterChange.emit();
  }

  /** Changing the field invalidates the operator and any captured arguments. */
  setConditionField(condition: FilterCondition, fieldId: string): void {
    condition.field = fieldId || null;
    const ops = this.opsOf(condition);
    condition.op = ops.length ? ops[0].id : null;
    condition.value = this.fieldOf(condition)?.type === 'flag' ? true : null;
    condition.value2 = null;
    this.emit();
  }

  setConditionOp(condition: FilterCondition, op: any): void {
    condition.op = op || null;
    if (this.argCount(condition) < 2) {
      condition.value2 = null;
    }
    this.emit();
  }

  onConditionValueChange(): void {
    this.emit();
  }

  private emit(): void {
    this.conditionsChange.emit(this.conditions);
    this.filterChange.emit();
  }
}
